import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { MoreVertical, Pencil, Trash2, Eye } from "lucide-react";
import { cn } from "../../lib/cn";

/**
 * ⋮ menu with fixed positioning (portal) so overflow tables do not shift.
 * Pass only the handlers you need; omitted items are hidden.
 */
export function CrudActionsMenu({
  ariaLabel = "Row actions",
  onEdit,
  onDelete,
  onView,
  editLabel = "Edit",
  deleteLabel = "Delete",
  viewLabel = "View details",
}) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const wrapRef = useRef(null);
  const menuRef = useRef(null);
  const btnRef = useRef(null);

  const updateMenuPosition = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setMenuPos({
      top: r.bottom + 4,
      right: Math.max(8, window.innerWidth - r.right),
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPosition();
    const onResize = () => updateMenuPosition();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return;
    const closeOnScroll = () => setOpen(false);
    window.addEventListener("scroll", closeOnScroll, true);
    return () => window.removeEventListener("scroll", closeOnScroll, true);
  }, [open]);

  useEffect(() => {
    const close = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const itemClass =
    "flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-50";

  const menu =
    open &&
    createPortal(
      <ul
        ref={menuRef}
        role="menu"
        style={{
          position: "fixed",
          top: menuPos.top,
          right: menuPos.right,
          minWidth: "11rem",
        }}
        className="z-[200] overflow-hidden rounded-xl border border-slate-200/90 bg-white py-1 shadow-lg"
      >
        {onView ? (
          <li>
            <button
              type="button"
              role="menuitem"
              className={itemClass}
              onClick={() => {
                onView();
                setOpen(false);
              }}
            >
              <Eye className="h-4 w-4 shrink-0 text-slate-400" />
              {viewLabel}
            </button>
          </li>
        ) : null}
        {onEdit ? (
          <li>
            <button
              type="button"
              role="menuitem"
              className={itemClass}
              onClick={() => {
                onEdit();
                setOpen(false);
              }}
            >
              <Pencil className="h-4 w-4 shrink-0 text-slate-400" />
              {editLabel}
            </button>
          </li>
        ) : null}
        {onDelete ? (
          <li className={onView || onEdit ? "border-t border-slate-100 pt-1" : ""}>
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm font-semibold text-red-600 transition hover:bg-red-50"
              onClick={() => {
                onDelete();
                setOpen(false);
              }}
            >
              <Trash2 className="h-4 w-4 shrink-0" />
              {deleteLabel}
            </button>
          </li>
        ) : null}
      </ul>,
      document.body
    );

  const hasAny = onView || onEdit || onDelete;
  if (!hasAny) return null;

  return (
    <div className="flex justify-end" ref={wrapRef}>
      <button
        ref={btnRef}
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={ariaLabel}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-600 transition hover:bg-slate-100 hover:text-brand",
          open && "bg-slate-100 text-brand"
        )}
      >
        <MoreVertical className="h-4 w-4" strokeWidth={2.25} />
      </button>
      {menu}
    </div>
  );
}
